import axios from "axios";
import { apiConnection } from "@/services/api-connection";
import router from "@/router";
import { mainStore, themeStore } from "@/main";
import { storageManager } from "@/services/storage/storage-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { useUserStore } from "@/store/user/user";
import { loginManager } from "@/services/security/login-manager";

const URL_INFOS = "/app/user/me";
const URL_ONE_USER = "/app/user/info/";
// const URL_USER = "/ufauser/";
const URL_CHECK = "/app/user/verify-user";
const URL_POST = "/app/user/put/";
const URL_POST_PICTURE = "/post-picture";
const URL_PERMISSIONS_ADMIN = "/app/user/permissions";
const URL_PERMISSIONS_STUDEA_MANAGER = "/app/studea-manager/permissions";
const URL_LOGOUT = "/app/user/logout";

class UserManager {
  PROFILE_APPRENTICE = 1;
  PROFILE_TUTOR = 2;
  PROFILE_APPRENTICE_MASTER = 3;
  PROFILE_STUDEA_MANAGER = 4;
  loggingOut = false;

  // Le back réserve /certify au MAP, et au tuteur pédagogique "si autorisé" (règle métier vérifiée
  // côté back, qui renvoie un 403 le cas échéant) : le front ne modélise pas cette nuance. Source
  // unique de cette règle, consommée par ContractActivitySignatureComponent, ContractActivityComponent
  // et DashboardView (ST154-TA-4911) pour éviter sa duplication.
  canCertifyContractActivity(profile) {
    return profile === this.PROFILE_APPRENTICE_MASTER || profile === this.PROFILE_TUTOR;
  }

  loadUserInfos() {
    return apiConnection
      .get(URL_INFOS, {}, true)
      .then((response) => response)
      .catch((res) => {
        // Plateforme en maintenance : le 503 ne dit rien de la validité de la session,
        // déconnecter ici renverrait l'utilisateur sur Login au lieu de l'écran dédié.
        if (503 === res?.status) {
          return;
        }
        // En lien magique, /app/user/me n'est pas whitelisté côté back pour toutes les routes
        // (questionnaire, first-login) : un 401 ici n'est pas une perte de session, il faut
        // propager le rejet pour que le garde router dégrade proprement, sans déconnecter
        // un utilisateur qui n'a jamais eu de session à proprement parler.
        if (apiConnection.getMagicLinkToken()) {
          return Promise.reject(res);
        }
        if (!res.success) {
          userManager.logout(true);
        }
      });
  }

  loadUser(id) {
    return apiConnection.get(URL_ONE_USER + id).then((response) => response);
  }

  loadPermissions(trainingCourse = null, forceStudeaManager = false) {
    const user = useUserStore().user;
    if (trainingCourse || (!trainingCourse && forceStudeaManager)) {
      return apiConnection
        .get(URL_PERMISSIONS_STUDEA_MANAGER + (trainingCourse ? "/" + trainingCourse : ""))
        .then((response) => response);
    } else if (user.target === "admin") {
      return apiConnection.get(URL_PERMISSIONS_ADMIN).then((response) => response);
    }
    return new Promise((resolve) => resolve(false));
  }

  checkUsername = (username) => {
    return apiConnection
      .get(
        URL_CHECK,
        {
          email: username,
        },
        false,
      )
      .then((response) => response);
  };

  logout(withReferer = false) {
    // Garde de ré-entrance : une déconnexion en échec (revokeSession qui 401 sur un token
    // déjà absent) peut redéclencher logout() via le mécanisme de retry JWT. Sans cette
    // garde, revokeSession() rappelle logout() qui rappelle revokeSession() en boucle
    // (cf. bug de la navigation "précédente" du navigateur après une déconnexion).
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;

    // Fire-and-forget : révocation du token côté serveur (RefreshTokenRevocationService,
    // ST143-TI-17697) — sans cet appel, un refresh token "rester connecté" (7 jours) resterait
    // valide après la déconnexion. Simple nettoyage, jamais une précondition : un échec (token
    // déjà expiré/invalide, hors-ligne...) ne doit jamais empêcher ni retarder la déconnexion
    // locale, ni afficher d'erreur.
    void this.revokeSession();

    storageManager.clear();
    mainStore.setAppLoading(true);
    mainStore.initStore();

    let query = {};
    if (withReferer) {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      params.delete("magic-link");
      if (url.pathname !== "/login") {
        query = { referer: url.pathname + params };
      }
    }

    setTimeout(() => {
      // replace, pas push : ne pas empiler d'entrée d'historique sur la page protégée
      // quittée, sinon un retour arrière navigateur y renvoie et redéclenche tout le
      // cycle de vérification d'auth (checkRoleAccess -> refresh -> 401 -> logout...).
      router.replace({ name: "Login", query: query });
      mainStore.setAppLoading(false);
      if (Object.keys(query).length && !withReferer) {
        notificationManager.showAlert(
          "info",
          i18n.global.t("reconnect_error"),
          i18n.global.t("reconnect_error_message"),
          true,
        );
      }
      this.loggingOut = false;
    }, 300);
  }

  async revokeSession() {
    // Aucun access token en mémoire : rien à révoquer côté serveur (déjà nettoyé par un
    // précédent logout()), ne pas émettre un appel voué à 401. Aucune plateforme (supprimée,
    // cf. errorManager.handleRemovedPlatform) : le refresh partirait sur l'hôte du webview.
    if (!loginManager.token || !loginManager.apiPath) {
      return;
    }

    // Appel en axios brut, volontairement hors du pipeline apiConnection/errorManager :
    // celui-ci déclenche un refresh JWT automatique puis rejoue l'appel sur un 401, et
    // sur un second échec rappelle logout() -> revokeSession() -> nouveau 401 -> boucle
    // infinie (observée en navigation "précédente" après déconnexion, cf. ticket).
    // Cet appel est un best-effort de nettoyage : un échec ne doit jamais ni retarder
    // la déconnexion locale, ni rappeler logout().
    try {
      // La route /app/user/logout exige IS_AUTHENTICATED_FULLY (JWT), le refresh token
      // seul ne l'authentifie pas. Le TTL de l'access token (5 min) expire bien avant le
      // timeout d'inactivité (25 min) : on (re)garantit donc un access token valide avant
      // l'appel, via le mécanisme de refresh dédupliqué de loginManager (refreshPromise,
      // rotation single_use côté serveur) plutôt qu'un appel /token/refresh parallèle qui
      // consommerait un refresh token déjà en vol.
      const refreshResult = await loginManager.refreshConnection();
      if (!refreshResult || !refreshResult.success || !loginManager.token) {
        return;
      }

      // Corps `{}` et non `null` : avec un Content-Type JSON forcé, axios sérialise `null`
      // en la chaîne "null", que le BodyListener de FOSRest rejette en 400 "Invalid json
      // message received". La révocation serveur ne partait alors jamais (ST143-TI-18329).
      await axios.post(
        `${loginManager.apiPath}/${themeStore.locale}${URL_LOGOUT}`,
        {},
        {
          headers: {
            "Content-Type": "application/json;charset=utf-8",
            Authorization: `Bearer ${loginManager.token}`,
          },
          // Sans cette option le navigateur ignore le `Set-Cookie` de purge renvoyé par la route
          // de logout : le cookie HttpOnly du refresh token (ST143-TI-18073) survivrait à la
          // déconnexion, storageManager.clear() ne pouvant pas l'atteindre depuis JS.
          withCredentials: true,
        },
      );
    } catch {
      // Ignoré volontairement : nettoyage best-effort, la déconnexion locale continue
      // quoi qu'il arrive (cf. commentaire dans logout()).
    }
  }

  putUserInfos(id, datas) {
    return apiConnection.put(URL_POST + id, datas).then((response) => response);
  }

  postUserPicture(data, folder) {
    return apiConnection
      .post(
        URL_POST_PICTURE,
        {
          data: data.data,
          filename: data.fileName,
          type: data.type,
          folder: folder,
        },
        true,
      )
      .then((response) => response);
  }

  getProfileKey(profile) {
    let key;
    switch (profile) {
      case this.PROFILE_APPRENTICE:
        key = "apprentice";
        break;
      case this.PROFILE_TUTOR:
        key = "tutor";
        break;
      case this.PROFILE_APPRENTICE_MASTER:
        key = "apprenticeMaster";
        break;
      case this.PROFILE_STUDEA_MANAGER:
        key = "studeaManager";
        break;
    }
    return key;
  }

  getProfileByName(profileName) {
    let key;
    switch (profileName) {
      case "apprentices":
        key = this.PROFILE_APPRENTICE;
        break;
      case "tutors":
        key = this.PROFILE_TUTOR;
        break;
      case "apprenticeMasters":
        key = this.PROFILE_APPRENTICE_MASTER;
        break;
      case "studeaManagers":
        key = this.PROFILE_STUDEA_MANAGER;
        break;
    }
    return key;
  }

  /**
   * ST63-TA-4535 : un contrat porte trois maîtres d'apprentissage, `operationalApprenticeMaster`
   * (MAP1), `apprenticeMaster` (MAP2) et `apprenticeMaster2` (MAP3), tous de même niveau de droits
   * par décision produit. Un seul identifiant correspondant suffit.
   *
   * `triad.apprenticeMaster` est une VirtualProperty back qui renvoie
   * `activeContract.operationalApprenticeMaster` (le champ brut `Triad::$apprenticeMaster` est
   * `@Serializer\Exclude`) : il fait donc doublon avec MAP1 et n'est conservé que par sécurité si
   * cette VirtualProperty venait à changer de source.
   *
   * `uniqueApprenticeMaster` / `uniqueApprenticeMaster2` sont les seules clés MAP2/MAP3 présentes
   * dans `GROUP_APPLICATION_TRIAD_LIST` (où `apprenticeMaster` / `apprenticeMaster2` ne le sont
   * pas). Aujourd'hui `checkUserProfile()` n'est appelé qu'avec le trinôme de `triadStore`, chargé
   * par `/app/triad/{id}` en `GROUP_APPLICATION_TRIAD` qui porte bien les trois clés ; les tester
   * en plus évite qu'un trinôme issu de la liste ne fasse retomber un MAP2/MAP3 sur
   * PROFILE_STUDEA_MANAGER, c'est-à-dire sur une vue plus ouverte. Elles ne renvoient jamais qu'une
   * des trois FK du contrat ou null, donc elles ne peuvent accorder le profil à personne d'autre.
   *
   * `triad.activeContract` est fourni par le back pour CE trinôme : `Triad::getActiveContract()`
   * n'itère que `$this->getContracts()`, l'appartenance au trinôme est donc structurelle.
   *
   * Le statut du contrat n'est volontairement pas regardé : le back distingue
   * `Triad::isApprenticeMaster()` (permissif) de `isApprenticeMasterOfActiveContract()`
   * (fail-closed sur BROKEN, consommé par TriadVoter pour le livret). L'affichage reproduit le
   * permissif, comme en production aujourd'hui ; le cloisonnement réel reste au Voter.
   */
  isApprenticeMaster(user, triad) {
    const apprenticeMasterIds = [
      triad?.apprenticeMaster?.id,
      triad?.activeContract?.operationalApprenticeMaster?.id,
      triad?.activeContract?.apprenticeMaster?.id,
      triad?.activeContract?.apprenticeMaster2?.id,
      triad?.activeContract?.uniqueApprenticeMaster?.id,
      triad?.activeContract?.uniqueApprenticeMaster2?.id,
    ];

    return apprenticeMasterIds.some((id) => id !== undefined && id !== null && id === user?.id);
  }

  checkUserProfile(user, triad) {
    // Sans utilisateur ou sans triade identifiés (contexte lien magique notamment), aucun profil ne
    // peut être déterminé : renvoyer null plutôt que PROFILE_STUDEA_MANAGER, qui ouvrirait par
    // défaut les contenus réservés aux managers CFA.
    if (!user || !user.id || !triad || !Object.keys(triad).length) {
      return null;
    }
    const secondaryUser = Array.isArray(triad?.triadUsers)
      ? triad.triadUsers.find((triadUser) => triadUser.user?.id === user.id)
      : null;
    if (secondaryUser && Object.keys(secondaryUser).length > 0) {
      return secondaryUser.userType;
    }
    if (triad.apprentice && user.id === triad.apprentice.id) {
      return this.PROFILE_APPRENTICE;
    } else if (triad.tutor && user.id === triad.tutor.id) {
      return this.PROFILE_TUTOR;
    } else if (this.isApprenticeMaster(user, triad)) {
      return this.PROFILE_APPRENTICE_MASTER;
    } else {
      return this.PROFILE_STUDEA_MANAGER;
    }
  }

  findUserType(userType, triad) {
    if (triad.triadUsers) {
      return triad.triadUsers.filter((triadUser) => triadUser.userType === userType);
    }
  }

  regenerateUserName($this, urlCall) {
    const param = {
      lastName: $this.tabs[0]?.models?.lastName?.vars?.value,
      firstName: $this.tabs[0]?.models?.firstName?.vars?.value,
    };
    $this.loading = true;
    return apiConnection
      .post(urlCall, param)
      .then((data) => {
        notificationManager.showNotification("success", i18n.global.t("regenerate_succes"));
        if ($this.tabs[0].models["username"]?.vars?.value) {
          $this.tabs[0].models["username"].vars.value = data;
        }
      })
      .finally(() => {
        $this.loading = false;
      });
  }

  async activateAccount() {
    return await apiConnection.get("/app/user/form/first-login");
  }

  async postUserFirstConnection(data) {
    return await apiConnection.post("/app/user/post/first-login", data);
  }
}

export const userManager = new UserManager();
